import { Reducer } from '@reduxjs/toolkit';
import { FluxStandardAction } from 'redux-promise-middleware';
import { ImportError, ImportLog, ImportTypes } from '../../../types/import_export';

export type ParentImportLogsState = {
  importLogs: ImportLog[];
  fetchingParentImportLogs: boolean;
  errorLogDetails: ImportError[];
  imports: ImportState[];
  vcdbImportLogId: number | null;
  fetchingParentImportErrors: boolean;
};

const initialState = {
  importLogs: [],
  fetchingParentImportLogs: false,
  errorLogDetails: [],
  imports: [],
  vcdbImportLogId: null,
  fetchingParentImportErrors: false,
};

export enum CurrentImportState {
  'UPLOAD' = 'UPLOAD',
  'IMPORT' = 'IMPORT',
  'DONE' = 'DONE',
}

export type ImportState = {
  id: string;
  logId: number | null;
  fileName: string;
  fileTypeId?: number;
  importType: ImportTypes;
  currentState: CurrentImportState;
  showNotification: boolean;
  uploadState: { progress: number; failed: boolean };
  importState?: {
    progress: number;
    subProgress: number;
    status: string;
    errors: boolean;
    completed: boolean;
    statusId: number | null;
  };
};

const getImportTypeFromSourceId = (sourceId: number) => {
  if (sourceId === 1) {
    return ImportTypes.ACES;
  }
  if (sourceId === 2) {
    return ImportTypes.PIES;
  }
  if (sourceId === 5) {
    return ImportTypes.ASSET;
  }
  return ImportTypes.EXCEL;
};

const formatImportLog = (log: any) => {
  const importCompleted = !!log.completed_at;
  const importState: ImportState = {
    id: `${log.id}`,
    logId: log.id,
    fileName: log.file_name,
    fileTypeId: log.file_type_id,
    importType: getImportTypeFromSourceId(log.source_id),
    showNotification: !importCompleted,
    currentState: importCompleted ? CurrentImportState.DONE : CurrentImportState.IMPORT,
    uploadState: { progress: 0, failed: false },
    importState: {
      progress: log.progress,
      subProgress: log.sub_progress,
      status: log.status,
      errors: log.errors,
      completed: importCompleted,
      statusId: log.status_id,
    },
  };
  return importState;
};

const reducer: Reducer<ParentImportLogsState, FluxStandardAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case 'FETCH_PARENT_IMPORT_LOGS_PENDING': {
      return { ...state, importLogs: [], fetchingParentImportLogs: true };
    }
    case 'FETCH_PARENT_IMPORT_LOGS_FULFILLED': {
      return {
        ...state,
        importLogs: action.payload.data,
        fetchingParentImportLogs: false,
      };
    }
    case 'FETCH_PARENT_IMPORT_LOG_DETAILS_JSON_PENDING': {
      return { ...state, errorLogDetails: [], fetchingParentImportErrors: true };
    }
    case 'FETCH_PARENT_IMPORT_LOG_DETAILS_JSON_FULFILLED': {
      return { ...state, errorLogDetails: action.payload.data, fetchingParentImportErrors: false };
    }
    case '@IMPORTER/FETCH_LATEST_PARENT_IMPORT_LOGS_FULFILLED': {
      const imports = action.payload.data;
      const formatedImports = imports.map((singleImport: any) => {
        return formatImportLog(singleImport);
      });
      return {
        ...state,
        imports: formatedImports,
      };
    }
    case '@IMPORTER/PARENT_UPLOAD_FINISHED': {
      const importId = action.payload.id;
      const logId = action.payload.logId;
      const vcdbImportLogId: number | null = action.payload.vcdbImportLogId;

      return {
        ...state,
        vcdbImportLogId,
        imports: state.imports.map((importValue: ImportState) => {
          if (importValue.id === importId) {
            return {
              ...importValue,
              currentState: CurrentImportState.IMPORT,
              logId,
              uploadState: { progress: 100 },
            };
          }
          return importValue;
        }),
      };
    }
    case '@IMPORTER/PARENT_CREATE_NEW_UPLOAD': {
      const { id, importType, fileName } = action.payload;
      const importState: ImportState = {
        id,
        importType,
        logId: null,
        fileName,
        showNotification: true,
        currentState: CurrentImportState.UPLOAD,
        uploadState: { progress: 0, failed: false },
      };
      return {
        ...state,
        imports: [...state.imports, importState],
      };
    }
    case '@IMPORTER/PARENT_UPDATE_UPLOAD_PROGRESS': {
      const { progress, id } = action.payload;
      return {
        ...state,
        imports: state.imports.map((importValue: ImportState) => {
          if (importValue.id === id) {
            return {
              ...importValue,
              uploadState: { progress: progress > 99 ? 99 : progress },
            };
          }
          return importValue;
        }),
      };
    }
    case '@IMPORTER/PARENT_UPLOAD_FAILED': {
      const importId = action.payload.id;
      return {
        ...state,
        imports: state.imports.map((importValue: ImportState) => {
          if (importValue.id === importId) {
            return {
              ...importValue,
              currentState: CurrentImportState.UPLOAD,
              uploadState: { failed: true },
            };
          }
          return importValue;
        }),
      };
    }
    case '@IMPORTER/PARENT_CLOSE_NOTIFICATION': {
      const { id } = action.payload;
      return {
        ...state,
        imports: state.imports.map(importValue => {
          if (importValue.id === id) {
            return {
              ...importValue,
              showNotification: false,
            };
          }
          return importValue;
        }),
      };
    }
    case '@IMPORTER/PARENT_OPEN_NOTIFICATION': {
      const { id } = action.payload;
      return {
        ...state,
        imports: state.imports.map(importValue => {
          return importValue.logId === id
            ? {
                ...importValue,
                showNotification: true,
              }
            : importValue;
        }),
      };
    }
    case '@IMPORTER/FETCH_RUNNING_PARENT_IMPORT_LOGS_FULFILLED': {
      const importLogs = action.payload.data;
      const importLogIds = importLogs.map((log: any) => log.id);
      return {
        ...state,
        imports: state.imports.map(importValue => {
          if (importLogIds.includes(importValue.logId)) {
            const log = importLogs.find((log: any) => log.id === importValue.logId);
            const importCompleted = !!log.completed_at;
            return {
              ...importValue,
              fileTypeId: log.file_type_id,
              currentState: importCompleted ? CurrentImportState.DONE : CurrentImportState.IMPORT,
              importState: {
                progress: log.progress,
                subProgress: log.sub_progress,
                completed: importCompleted,
                status: log.status,
                errors: log.error_count > 0,
                statusId: log.status_id,
              },
            };
          }
          return importValue;
        }),
      };
    }
  }
  return state;
};

export default reducer;
